'use client';
import { OrderDocumentItem } from 'bff';
import { FilesDisplayer, FilesDisplayerItem } from './FilesDisplayer';
import { Separator } from 'ui';

// F2.4 — documentación de una orden, separada en dos secciones según `uploader`:
// "Documentación de la compañía" (insurer) y "Documentación del proveedor"
// (provider). Cada sección se renderiza con `FilesDisplayer` (vista grid|list).
// El botón de subir NO vive acá: cada app lo pone en el encabezado de la
// pestaña, para que quede en el mismo lugar de los dos lados (antes caía junto
// al switch de vista de "su" sección, o sea arriba para el insurer y abajo para
// el proveedor).
const authorName = (
  uploadedBy: OrderDocumentItem['uploaded_by'],
): string | null => {
  if (!uploadedBy) return null;
  const name = [uploadedBy.first_name, uploadedBy.last_name]
    .filter(Boolean)
    .join(' ');
  return name || null;
};

const toDisplayerItem = (item: OrderDocumentItem): FilesDisplayerItem => ({
  id: item.id,
  name: item.file.original_name,
  size: Number(item.file.size),
  mimetype: item.file.mimetype,
  url: item.file.url,
  previewURL: item.file.previewURL,
  uploadedBy: authorName(item.uploaded_by),
  uploadedAt: item.file.created_at,
});

export const OrderDocumentationList = ({
  items,
  companyTitle = 'Documentación subida por la compañía',
  providerTitle = 'Documentación subida por el proveedor',
  renderCompanyItemActions,
}: {
  items: OrderDocumentItem[];
  companyTitle?: string;
  providerTitle?: string;
  // order_document_import — per-document slot for the COMPANY section only: each
  // party manages its own documentation. Omitted = read-only (provider app).
  renderCompanyItemActions?: (item: OrderDocumentItem) => React.ReactNode;
}) => {
  const companyItems = items.filter((item) => item.uploader === 'insurer');
  const companyDocs = companyItems.map(toDisplayerItem);
  const providerDocs = items
    .filter((item) => item.uploader === 'provider')
    .map(toDisplayerItem);

  const companyItemById = new Map(companyItems.map((item) => [item.id, item]));

  if (companyDocs.length === 0 && providerDocs.length === 0) {
    return (
      <p className='text-sm text-muted-foreground px-2 py-2'>
        No hay documentación adicional subida por el momento.
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-4 bg-white px-2 py-2'>
      {companyDocs.length > 0 ? (
        <FilesDisplayer
          title={companyTitle}
          files={companyDocs}
          renderItemActions={
            renderCompanyItemActions &&
            ((file) => {
              const item = companyItemById.get(file.id);
              return item ? renderCompanyItemActions(item) : null;
            })
          }
        />
      ) : (
        <p className='text-sm text-muted-foreground'>
          <span className='font-semibold'>{companyTitle}</span> no agregó
          documentación adicional por el momento.
        </p>
      )}
      {providerDocs.length > 0 ? (
        <FilesDisplayer title={providerTitle} files={providerDocs} />
      ) : (
        <p className='text-sm text-muted-foreground'>
          <span className='font-semibold'>{providerTitle}</span> no agregó
          documentación adicional por el momento.
        </p>
      )}
    </div>
  );
};
